/**
 * Linear resampler from the stethoscope's capture rate to the AudioContext rate.
 *
 * Ported from UI_NEW's `stethoscope-audio.service.ts` with the algorithm intact.
 * Two properties are load-bearing and must not be "simplified":
 *
 * 1. Linear interpolation is sufficient here. Diagnostic content sits below
 *    ~2 kHz, far under any Nyquist limit in play, so interpolation error lands in
 *    a band nobody listens to.
 *
 * 2. `resamplePos` carries the fractional read position across chunk boundaries.
 *    Without it every chunk restarts at phase 0 and adds a periodic discontinuity
 *    at roughly 11 Hz — inside the band a doctor is listening to. This is the
 *    single most important line in the file and the reason test 1 exists.
 */
export class Resampler {
  /** captureRate / contextRate. 1 means pass-through. */
  private ratio = 1;

  /**
   * Fractional read offset into the *next* input chunk. Survives across calls —
   * that is the whole point.
   *
   * May be negative, meaning the next output sample falls between the previous
   * chunk's last sample and this chunk's first. That case is why `previous`
   * exists.
   */
  private position = 0;

  /**
   * The previous chunk's final sample.
   *
   * Needed whenever `position` is negative: the interpolation's left-hand
   * neighbour then lives in the chunk that has already been consumed. Without
   * it that read has to be clamped to the current chunk's first sample, which
   * flattens one sample at every seam — a small discontinuity roughly twelve
   * times a second, right inside the band a clinician is listening to.
   */
  private previous = 0;

  private sourceRate = 0;

  /**
   * Configure from the rate the capture side reports.
   *
   * Called lazily on the first non-empty chunk rather than at setup: at that
   * point capture is provably live and the negotiated rate is known, which
   * avoids the first-chunk race where the rate is still 0.
   */
  configure(captureRate: number, contextRate: number): void {
    if (!Number.isFinite(captureRate) || captureRate <= 0) {
      // "Unknown" — assume the context rate and pass through rather than
      // guessing. Pitch stays correct if the assumption holds, and the caller
      // logs the ambiguity.
      this.sourceRate = contextRate;
      this.ratio = 1;
      this.position = 0;
      this.previous = 0;
      return;
    }

    this.sourceRate = captureRate;
    this.ratio = captureRate / contextRate;
    this.position = 0;
    this.previous = 0;
  }

  getSourceRate(): number {
    return this.sourceRate;
  }

  getRatio(): number {
    return this.ratio;
  }

  /** True when the capture rate differs from the context rate. */
  isResampling(): boolean {
    return this.ratio !== 1;
  }

  /**
   * Resample one chunk, continuing the phase from the previous call.
   *
   * Returns the input array unchanged when no resampling is needed. Callers must
   * therefore treat the result as read-only, or pass a freshly allocated chunk.
   */
  process(input: Float32Array): Float32Array {
    const ratio = this.ratio;
    if (ratio === 1 || input.length === 0) {
      return input;
    }

    const last = input.length - 1;

    // Emit only the samples whose interpolation interval is fully available:
    // the largest j with position + j*ratio <= last. Using `ceil` here — as the
    // original did — asks for one sample beyond the input, which then has to be
    // clamped, duplicating the final value at every chunk boundary.
    const count = Math.max(0, Math.floor((last - this.position) / ratio) + 1);
    const out = new Float32Array(count);

    for (let j = 0; j < count; j++) {
      const pos = this.position + j * ratio;
      const i = Math.floor(pos);
      const frac = pos - i;
      const a = this.sampleAt(input, i, last);
      const b = this.sampleAt(input, i + 1, last);
      out[j] = a + (b - a) * frac;
    }

    // Whatever is left over of the last input sample starts the next chunk.
    this.position = this.position + count * ratio - input.length;
    this.previous = input[last];
    return out;
  }

  /** Read `index`, reaching back into the previous chunk when it is negative. */
  private sampleAt(input: Float32Array, index: number, last: number): number {
    if (index < 0) return this.previous;
    return input[Math.min(index, last)];
  }

  /** Reset phase. Only correct at a genuine stream discontinuity. */
  reset(): void {
    this.position = 0;
    this.previous = 0;
  }
}
